import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { ExternalResource, isError, isLoaded, isLoading } from '@next/arable-lib/external-resource';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { map, Observable, of } from 'rxjs';
import { EmptyWidgetComponent } from 'src/app/shared/components/empty-widget.component';
import { LoadingCardComponent } from 'src/app/shared/components/loading-card.component/loading-card.component';
import { FieldObservationDialogDto, FieldObservationImage } from '../../shared/models/models';
import { DataUriPipe } from '../../shared/pipes/data-uri.pipe';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { toLocaleSignal } from '@app/shared/utils';
import {
  FieldObservationDialogComponent,
  FieldObservationDialogData,
} from '../dialogs/field-observation-dialog/field-observation-dialog.component';
import { MatDialog } from '@angular/material/dialog';

export interface FieldObservationImageGroup {
  key?: string;
  date: string;
  observationId?: number;
  images: FieldObservationImage[];
  startIndex: number;
}

@Component({
  selector: 'app-tga-field-observation-images',
  templateUrl: './tga-field-observation-images.component.html',
  styleUrl: './tga-field-observation-images.component.scss',
  imports: [
    AsyncPipe,
    TranslatePipe,
    DatePipe,
    EmptyWidgetComponent,
    LoadingCardComponent,
    FormsModule,
    MatIcon,
    DataUriPipe,
  ],
})
export class TgaFieldObservationImagesComponent {
  private readonly localeService = inject(LocaleService);
  private readonly matDialog = inject(MatDialog);
  fieldObservationImages$ = input<Observable<ExternalResource<FieldObservationImage[]>>>(of({ state: 'idle' }));
  title = input<string>('');
  cropFieldId = input<number | null>(null);
  cropFieldName = input<string>('');

  protected readonly isLoading = isLoading;
  protected readonly isLoaded = isLoaded;
  protected readonly isError = isError;

  protected readonly emptyMessage = computed(() =>
    this.localeService.translate('TGA.Advice.Cultivation.FieldObservationImages.Empty'),
  );
  protected readonly remarkPrefix = computed(() =>
    this.localeService.translate('TGA.Advice.Cultivation.FieldObservationImages.Prefix'),
  );
  protected readonly noImageMessage = computed(() =>
    this.localeService.translate('TGA.Advice.Cultivation.FieldObservationImages.NoImage'),
  );

  protected readonly locale = toLocaleSignal(this.localeService);

  readonly groupedFieldObservationImages$ = computed(() =>
    this.fieldObservationImages$().pipe(
      map((resource) => {
        if (!isLoaded(resource) || !resource.data) {
          return resource as ExternalResource<FieldObservationImageGroup[]>;
        }
        return {
          ...resource,
          data: this.groupByObservation(resource.data),
        };
      }),
    ),
  );

  private groupByObservation(images: FieldObservationImage[]): FieldObservationImageGroup[] {
    const groupMap = new Map<
      string,
      { key: string; date: string; observationId?: number; images: FieldObservationImage[] }
    >();
    for (const image of images) {
      const key = image.observationId != null ? `observation-${image.observationId}` : image.createdAt;
      const existing = groupMap.get(key);
      if (existing) {
        existing.images.push(image);
      } else {
        groupMap.set(key, {
          key,
          date: image.createdAt,
          observationId: image.observationId,
          images: [image],
        });
      }
    }
    return [...groupMap.values()]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((group, i, arr) => ({
        key: group.key,
        date: group.date,
        observationId: group.observationId,
        images: group.images,
        startIndex: arr.slice(0, i).reduce((sum, g) => sum + g.images.length, 0),
      }));
  }

  protected groupImagesWithContent(group: FieldObservationImageGroup): FieldObservationImage[] {
    return group.images.filter((image) => Boolean(image.imageBase64));
  }

  protected onImageClicked(
    groups: FieldObservationImageGroup[],
    groupStartIndex: number,
    _imageIndexInGroup: number,
  ): void {
    const titleBase = this.localeService.translate('TGA.Advice.Cultivation.FieldObservationImages.Title');
    const name = this.cropFieldName();
    const title = name ? `${titleBase} — ${name}` : titleBase;

    const initialObservationIndex = groups.findIndex((g) => g.startIndex === groupStartIndex);

    const fieldObservations: FieldObservationDialogDto[] = groups.map((group) => ({
      images: this.groupImagesWithContent(group).map((img) => ({
        imageBase64: img.imageBase64,
        contentType: img.contentType,
        alt: img.description,
      })),
      observationDate: group.date,
      cropFieldName: name,
      remarks: group.images[0]?.description,
      observations: group.images[0]?.observations ?? [],
    }));

    const dialogData: FieldObservationDialogData = {
      title,
      cropFieldName: name,
      fieldObservations,
      initialObservationIndex: Math.max(initialObservationIndex, 0),
      confirmButton: false,
      cancelButton: false,
    };

    this.matDialog.open(FieldObservationDialogComponent, {
      data: dialogData,
      disableClose: false,
      autoFocus: false,
      width: '96vw',
      maxWidth: '1180px',
      height: '88vh',
      maxHeight: '760px',
      panelClass: 'field-observation-dialog-panel',
    });
  }
}
